import { useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * useForecastSync — keeps formik forecast entries in sync with product categories.
 *
 * This hook runs in ProjectDrawerSections (always mounted) so that forecast
 * entries are created/removed even when the Forecasts stepper section is not
 * active.  MUI StepContent uses `unmountOnExit: true`, which means the
 * ForecastsSection component (and its effects) are unmounted when the user is
 * on a different step.
 *
 * Responsibilities:
 *  1. Create forecast entries (with pre-distributed values) when a new product
 *     category is added to the project.
 *  2. Remove forecast entries when all products of a category are removed.
 *  3. Auto-redistribute forecasts for ALL categories when a category's
 *     effective total (override or product sum) changes.
 */
import { getTenantUnits } from '@core/configuration';
import type { ProjectFormikType } from '@core/formik/project';
import Enums from '@core/types/api/Enums';
import { Forecast } from '@core/types/api/Forecast/Forecast';
import { ParseServerDate } from '@core/utils/dateHelpers';
import { hasStringValue } from '@core/utils/equality';
import {
  getProductCategoriesInProject,
  sumProductQuantitiesByCategory,
} from '@core/utils/forecastDistribution';

// Helper shared with ForecastsSection — copied here to keep the hook
// self-contained (avoids importing from a UI component).
const isNonEmptyString = (value: unknown): value is string =>
  value !== null && value !== undefined && typeof value === 'string' && value.trim() !== '';

/**
 * Build empty forecast entries for `category`, using an existing category's
 * entries as a date-range template when available.
 */
const buildForecastsForCategory = (
  category: Enums.ProductCategory,
  availableCategories: Enums.ProductCategory[],
  currentForecasts: ProjectFormikType['forecasts'],
): ProjectFormikType['forecasts'] => {
  const templateCategory = availableCategories.find((cat) =>
    currentForecasts.some((f) => f.productCategory === cat),
  );

  if (templateCategory) {
    return currentForecasts
      .filter((f) => f.productCategory === templateCategory)
      .map((f) => ({
        ...f,
        productCategory: category,
        value: '',
      }));
  }

  return Forecast.fillForecasts({
    kind: Enums.ForecastKind.Project,
    currentForecasts: [],
    productCategory: category,
    tenantUnits: getTenantUnits(),
  }).map<ProjectFormikType['forecasts'][number]>((pf) => ({
    id: pf.id,
    kind: pf.kind,
    value: '',
    unit: pf.unit,
    productCategory: pf.productCategory,
    intervalLength: pf.intervalLength.toISO() ?? '',
    intervalStart: pf.intervalStart.toISO() ?? '',
  }));
};

/**
 * Return the effective total for a category: override value if set, otherwise
 * the sum of product quantities for that category.
 */
const getEffectiveCategoryTotal = (
  values: ProjectFormikType,
  category: Enums.ProductCategory,
): number => {
  let override: number | null | undefined = null;
  if (category === Enums.ProductCategory.ReadyMix) override = values.readyMixQuantityOverride;
  else if (category === Enums.ProductCategory.Aggregate)
    override = values.aggregateQuantityOverride;
  else if (category === Enums.ProductCategory.Asphalt) override = values.asphaltQuantityOverride;

  if (typeof override === 'number' && !Number.isNaN(override) && override > 0) {
    return override;
  }
  return sumProductQuantitiesByCategory(values.productsByPlants, category);
};

export const useForecastSync = (): void => {
  const formikBag = useFormikContext<ProjectFormikType>();

  // Track which categories we've already auto-distributed and the effective
  // total at the time of the last distribution.  When the total changes we
  // re-distribute automatically.
  const initializedCategoriesRef = useRef<Map<string, number>>(new Map());

  // Keep a ref to the latest forecasts so the effect can read them without
  // declaring formikBag.values.forecasts as a dependency (which would cause
  // the effect to re-trigger after its own setFieldValue call and interfere
  // with React state batching).
  const forecastsRef = useRef(formikBag.values.forecasts);
  useLayoutEffect(() => {
    forecastsRef.current = formikBag.values.forecasts;
  });

  useEffect(() => {
    const availableCategories = getProductCategoriesInProject(formikBag.values.productsByPlants);

    let updatedForecasts = [...forecastsRef.current];
    let needsUpdate = false;

    // ── 1. Create entries (with pre-distributed values) for new categories ──

    // Parse project dates once for distribution range checks.
    const initStartDate = formikBag.values.estimatedStartDate;
    const initEndDate = formikBag.values.estimatedEndDate;
    const datesAlreadySet =
      isNonEmptyString(initStartDate) &&
      isNonEmptyString(initEndDate) &&
      DateTime.fromISO(initStartDate).isValid &&
      DateTime.fromISO(initEndDate).isValid;

    let rangeStart: DateTime;
    let rangeEnd: DateTime;
    if (datesAlreadySet) {
      rangeStart = DateTime.fromISO(initStartDate);
      rangeEnd = DateTime.fromISO(initEndDate);
    } else {
      const now = DateTime.now();
      const isEarlyInMonth = now.day <= 15;
      rangeStart = isEarlyInMonth ? now.startOf('month') : now.plus({ months: 1 }).startOf('month');
      rangeEnd = rangeStart.plus({ months: 11 }).endOf('month');
    }

    const isInRange = (intervalStart: string): boolean => {
      const dt = DateTime.fromISO(intervalStart);
      return (
        dt.isValid &&
        dt.startOf('month') >= rangeStart.startOf('month') &&
        dt.startOf('month') <= rangeEnd.startOf('month')
      );
    };

    const distributionMethod = hasStringValue(formikBag.values.forecastDistributionMethod)
      ? formikBag.values.forecastDistributionMethod
      : 'monthly';

    /**
     * Distribute `total` across entries at `indices` inside `arr`, mutating
     * `arr` in place and returning true if any values were set.
     */
    const distributeInto = (
      arr: ProjectFormikType['forecasts'],
      indices: number[],
      total: number,
    ): boolean => {
      if (indices.length === 0 || total <= 0) return false;

      if (distributionMethod === 'monthly') {
        const base = Math.floor(total / indices.length);
        const remainder = total % indices.length;
        indices.forEach((idx, allocIdx) => {
          arr[idx] = {
            ...arr[idx],
            value: String(base + (allocIdx < remainder ? 1 : 0)),
          };
        });
      } else {
        // Quarterly distribution
        const quarterMap: Record<string, number[]> = {};
        indices.forEach((idx) => {
          const { month } = ParseServerDate(arr[idx].intervalStart);
          const qKey = `Q${Math.floor((month - 1) / 3)}`;
          if (!quarterMap[qKey]) quarterMap[qKey] = [];
          quarterMap[qKey].push(idx);
        });

        const quarters = Object.values(quarterMap);
        const basePerQ = Math.floor(total / quarters.length);
        const remainderQ = total % quarters.length;

        quarters.forEach((qIndices, qIdx) => {
          const qVolume = basePerQ + (qIdx < remainderQ ? 1 : 0);
          const basePerM = Math.floor(qVolume / qIndices.length);
          const remainderM = qVolume % qIndices.length;
          qIndices.forEach((idx, mIdx) => {
            arr[idx] = {
              ...arr[idx],
              value: String(basePerM + (mIdx < remainderM ? 1 : 0)),
            };
          });
        });
      }
      return true;
    };

    availableCategories.forEach((category) => {
      const categoryKey = String(category);
      const hasForecasts = updatedForecasts.some((f) => f.productCategory === category);

      // Step 1: Create empty entries for categories that have none yet.
      if (!hasForecasts) {
        const newForecasts = buildForecastsForCategory(
          category,
          availableCategories,
          updatedForecasts,
        );
        if (newForecasts.length > 0) {
          updatedForecasts = [...updatedForecasts, ...newForecasts];
          needsUpdate = true;
        }
      }

      // Step 2: Distribute values into entries that have no distributed values
      // OR re-distribute when the category's effective total has changed.
      const categoryTotal = getEffectiveCategoryTotal(formikBag.values, category);

      if (initializedCategoriesRef.current.has(categoryKey)) {
        // ── Already initialized — check if the total changed ──
        const prevTotal = initializedCategoriesRef.current.get(categoryKey) ?? 0;
        if (Math.abs(prevTotal - categoryTotal) > Number.EPSILON && categoryTotal > 0) {
          // Total changed — redistribute this category across in-range months.
          const activeIndices: number[] = [];
          updatedForecasts.forEach((f, i) => {
            if (f.productCategory === category && isInRange(f.intervalStart)) {
              activeIndices.push(i);
            }
          });

          if (distributeInto(updatedForecasts, activeIndices, categoryTotal)) {
            needsUpdate = true;
            initializedCategoriesRef.current.set(categoryKey, categoryTotal);
          }
        }
        return;
      }

      // ── Not yet initialized ──
      if (categoryTotal <= 0) {
        return; // no quantity to distribute yet
      }

      const hasDistributedValues = updatedForecasts.some(
        (f) =>
          f.productCategory === category &&
          f.value !== '' &&
          f.value !== undefined &&
          f.value !== '0',
      );
      if (hasDistributedValues) {
        // Already has values (e.g. loaded from API) — mark as initialized.
        initializedCategoriesRef.current.set(categoryKey, categoryTotal);
        return;
      }

      // Find in-range entries for this category and distribute.
      const activeIndices: number[] = [];
      updatedForecasts.forEach((f, i) => {
        if (f.productCategory === category && isInRange(f.intervalStart)) {
          activeIndices.push(i);
        }
      });

      if (distributeInto(updatedForecasts, activeIndices, categoryTotal)) {
        needsUpdate = true;
        initializedCategoriesRef.current.set(categoryKey, categoryTotal);
      }
    });

    // ── 2. Remove entries for categories whose products were all removed ──

    const beforeCount = updatedForecasts.length;
    updatedForecasts = updatedForecasts.filter(
      (f) =>
        !f.productCategory ||
        availableCategories.includes(f.productCategory as Enums.ProductCategory),
    );
    if (updatedForecasts.length !== beforeCount) {
      needsUpdate = true;
      // Clean up initialized tracking for removed categories
      for (const [key] of initializedCategoriesRef.current) {
        if (!availableCategories.includes(key as Enums.ProductCategory)) {
          initializedCategoriesRef.current.delete(key);
        }
      }
    }

    if (needsUpdate) {
      formikBag.setFieldValue('forecasts', updatedForecasts);
    }
    // Note: forecastsRef is intentionally excluded from deps; including
    // formikBag.values.forecasts would cause the effect to re-trigger after
    // its own setFieldValue call.
    //
    // Note: estimatedStartDate and estimatedEndDate are also intentionally
    // omitted from deps. Date-range changes are handled by a dedicated effect
    // in ProjectDrawer.tsx (keyed on estimatedStartDate/estimatedEndDate) that
    // redistributes all forecast categories across the new range and clears
    // out-of-range values. This hook only reacts to product/override/method
    // changes; the dates are read here purely to determine the in-range window
    // for the initial distribution of newly added categories.
  }, [
    formikBag.values.productsByPlants,
    formikBag.values.readyMixQuantityOverride,
    formikBag.values.aggregateQuantityOverride,
    formikBag.values.asphaltQuantityOverride,
    formikBag.values.forecastDistributionMethod,
    formikBag.setFieldValue,
  ]);
};
